import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [hasSession, setHasSession] = useState<boolean | null>(null);

  useEffect(() => {
    // Supabase sets a recovery session from the email link hash params.
    supabase.auth.getSession().then(({ data: { session } }) => {
      setHasSession(!!session);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') setHasSession(true);
    });
    return () => subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    const { error: updateError } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (updateError) {
      setError(updateError.message);
      return;
    }
    setDone(true);
    setTimeout(() => navigate('/dashboard'), 2000);
  };

  return (
    <div className="min-h-screen bg-cream dark:bg-midnight-deep flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="card p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-gold to-gold-dark flex items-center justify-center">
              <i className="ri-key-2-line text-midnight text-lg" />
            </div>
            <div>
              <h1 className="font-display text-xl font-bold text-foreground-950">Set New Password</h1>
              <p className="text-xs text-foreground-500">Choose a strong password</p>
            </div>
          </div>

          {done ? (
            <div className="p-4 rounded-md bg-emerald-50 border border-emerald-200 text-sm text-emerald-800">
              Your password has been updated. Redirecting to your dashboard...
            </div>
          ) : hasSession === false ? (
            <div className="p-4 rounded-md bg-amber-50 border border-amber-200 text-sm text-amber-800">
              This reset link is invalid or has expired.{' '}
              <Link to="/forgot-password" className="font-medium underline">Request a new one</Link>.
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700">
                  {error}
                </div>
              )}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  New Password
                </label>
                <input
                  id="password"
                  type="password"
                  dir="ltr"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  placeholder="At least 8 characters"
                  className="w-full px-4 py-3 text-sm rounded-xl border border-background-300 bg-surface dark:bg-midnight-card text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/20 transition"
                />
              </div>
              <div>
                <label htmlFor="confirm" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  Confirm Password
                </label>
                <input
                  id="confirm"
                  type="password"
                  dir="ltr"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  minLength={8}
                  placeholder="Re-enter your password"
                  className="w-full px-4 py-3 text-sm rounded-xl border border-background-300 bg-surface dark:bg-midnight-card text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/20 transition"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full h-12 flex items-center justify-center text-sm whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? 'Updating...' : 'Update Password'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
